import { Button } from '@/components/Button';
import { OfflineBadge } from '@/components/OfflineBadge';
import { Spinner } from '@/components/Spinner';
import { StampChip } from '@/components/StampChip';
import { ThemeToggle } from '@/components/ThemeToggle';
import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import { useAuth } from '@/features/auth/useAuth';
import { FeedbackModal } from '@/features/feedback/FeedbackModal';
import { track } from '@/lib/telemetry';
import { MessageSquareText } from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

export function AppLayout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // page_view: fire on every route change within the authenticated shell.
  // routeTemplate is auto-attached by track() via currentRouteTemplate().
  useEffect(() => {
    track('page_view', {});
  }, [location.pathname]);

  return (
    <div style={{ minHeight: '100dvh', background: T.bg, color: T.text }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: T.bg,
          borderBottom: `1px solid ${T.border}`,
          // Safe-area-aware so the wordmark clears the iPhone notch and
          // we still touch the screen edge on devices without insets.
          paddingTop: `max(${SPACE.md}px, env(safe-area-inset-top))`,
          paddingBottom: `${SPACE.md}px`,
          paddingLeft: `max(${SPACE.md}px, env(safe-area-inset-left))`,
          paddingRight: `max(${SPACE.md}px, env(safe-area-inset-right))`,
        }}
      >
        {/* Inner row constrained to the same 720px column as <main> so
            the wordmark and sign-out button left-align with the page content. */}
        <div
          style={{
            maxWidth: 720,
            margin: '0 auto',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: SPACE.md,
          }}
        >
          <Link
            to="/vehicles"
            aria-label="Absolute Pinks — home"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 44,
              textDecoration: 'none',
            }}
          >
            <StampChip />
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.md }}>
            <OfflineBadge />
            <ThemeToggle />
            {user ? (
              <button
                type="button"
                onClick={() => setFeedbackOpen(true)}
                aria-label="Send feedback"
                style={{
                  background: T.card,
                  border: `1px solid ${T.border}`,
                  color: T.text,
                  borderRadius: RADIUS.pill,
                  width: 44,
                  height: 44,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: `background ${TRANSITION.fast}, border-color ${TRANSITION.fast}`,
                }}
              >
                <MessageSquareText size={18} aria-hidden="true" />
              </button>
            ) : null}
            {user ? (
              <Button size="sm" variant="ghost" onClick={() => void signOut()}>
                Sign out
              </Button>
            ) : null}
          </div>
        </div>
      </header>
      {feedbackOpen ? <FeedbackModal onClose={() => setFeedbackOpen(false)} /> : null}
      <main
        style={{
          paddingTop: SPACE.md,
          // Bottom inset so the home-indicator on iOS doesn't sit over
          // the last interactive element.
          paddingBottom: `max(${SPACE.md}px, env(safe-area-inset-bottom))`,
          paddingLeft: `max(${SPACE.md}px, env(safe-area-inset-left))`,
          paddingRight: `max(${SPACE.md}px, env(safe-area-inset-right))`,
          maxWidth: 720,
          margin: '0 auto',
        }}
      >
        {/* Route-level code splitting (router.tsx) lands lazy chunks here.
            Static routes resolve synchronously, so this fallback only ever
            shows while a split chunk (e.g. Genesis, Projects) is fetching. */}
        <Suspense
          fallback={
            <div style={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
              <Spinner label="Loading page" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
